import React, { useEffect, useState } from "react";
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  useColorModeValue,
  useToast,
  Spinner,
  IconButton,
} from "@chakra-ui/react";
import { CloseIcon } from "@chakra-ui/icons";
import PageShell from "../layout/PageShell";
import { collection, deleteDoc, doc, getDocs, serverTimestamp, setDoc } from "firebase/firestore";
import { db } from "../../firebase/config";

type Palette = { id: string; name: string; colors: string[] };

const BUILTIN_THEMES: { id: string; name: string; color: string }[] = [
  { id: "classic", name: "Classic", color: "#F97316" },
  { id: "modern-a", name: "Modern A", color: "#F97316" },
  { id: "modern-b", name: "Modern B", color: "#DC2626" },
  { id: "modern-c", name: "Modern C", color: "#16805D" },
  { id: "ocean-midnight", name: "Midnight", color: "#001219" },
  { id: "ocean-deep-teal", name: "Deep Teal", color: "#005f73" },
  { id: "ocean-lagoon", name: "Lagoon", color: "#0a9396" },
  { id: "ocean-seafoam", name: "Seafoam", color: "#94d2bd" },
  { id: "ocean-sand", name: "Sand", color: "#e9d8a6" },
  { id: "ocean-amber", name: "Amber", color: "#ee9b00" },
  { id: "ocean-copper", name: "Copper", color: "#ca6702" },
  { id: "ocean-rust", name: "Rust", color: "#bb3e03" },
  { id: "ocean-ember", name: "Ember", color: "#ae2012" },
  { id: "ocean-wine", name: "Wine", color: "#9b2226" },
];

const normalizeHex = (value: string) => {
  const h = value.trim().replace(/^#/, "");
  if (!/^[0-9a-fA-F]{6}$/.test(h)) return "";
  return `#${h.toLowerCase()}`;
};

const parseColorArray = (raw: string) => {
  const trimmed = raw.trim();
  if (!trimmed) return [];
  try {
    const parsed = JSON.parse(trimmed);
    if (Array.isArray(parsed)) {
      return parsed.map((color) => normalizeHex(String(color))).filter(Boolean);
    }
  } catch {
    /* Coolors arrays are JSON, but a loose list is accepted too. */
  }
  return (trimmed.match(/#?[0-9a-fA-F]{6}\b/g) || []).map(normalizeHex).filter(Boolean);
};

const slugify = (name: string) =>
  name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 48) || "palette";

const ColorPaletteSettings: React.FC = () => {
  const toast = useToast();
  const [palettes, setPalettes] = useState<Palette[]>([]);
  const [name, setName] = useState("");
  const [rawColors, setRawColors] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const bgColor = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.600");

  const load = async () => {
    const snap = await getDocs(collection(db, "colorPalettes"));
    setPalettes(
      snap.docs
        .map((entry) => ({
          id: entry.id,
          name: typeof entry.data()?.name === "string" ? entry.data().name : entry.id,
          colors: Array.isArray(entry.data()?.colors)
            ? entry.data().colors.filter((color: unknown): color is string => typeof color === "string")
            : [],
        }))
        .sort((a, b) => a.name.localeCompare(b.name))
    );
  };

  useEffect(() => {
    load()
      .catch(() => {
        toast({ title: "Could not load palettes", status: "error" });
      })
      .finally(() => setLoading(false));
  }, [toast]);

  const save = async () => {
    const paletteName = name.trim();
    const colors = parseColorArray(rawColors);
    if (!paletteName) {
      toast({ title: "Enter a palette name", status: "warning" });
      return;
    }
    if (colors.length < 1 || colors.length > 12) {
      toast({ title: "Paste 1 to 12 hex colors", status: "warning" });
      return;
    }
    setSaving(true);
    try {
      const base = slugify(paletteName);
      const taken = new Set(palettes.map((palette) => palette.id));
      let id = base;
      let n = 2;
      while (taken.has(id)) {
        id = `${base}-${n}`;
        n += 1;
      }
      await setDoc(doc(db, "colorPalettes", id), {
        name: paletteName,
        colors,
        updated: serverTimestamp(),
      });
      setName("");
      setRawColors("");
      await load();
      toast({ title: "Palette saved", status: "success" });
    } catch (error) {
      console.error(error);
      toast({ title: "Could not save palette", status: "error" });
    } finally {
      setSaving(false);
    }
  };

  const savedPalettes = palettes.filter((palette) => !BUILTIN_THEMES.some((builtin) => builtin.id === palette.id));

  const remove = async (id: string) => {
    try {
      await deleteDoc(doc(db, "colorPalettes", id));
      setPalettes((current) => current.filter((palette) => palette.id !== id));
    } catch (error) {
      console.error(error);
      toast({ title: "Could not delete palette", status: "error" });
    }
  };

  return (
    <PageShell
      title="Color palettes"
      description="Paste a Coolors array. Each color becomes its own guest theme, the same way Modern A, Modern B, and Modern C restyle the page."
    >
      <VStack align="stretch" spacing={6}>

        <Box bg={bgColor} borderWidth="1px" borderColor={borderColor} borderRadius="lg" p={4}>
          <VStack align="stretch" spacing={4}>
            <FormControl>
              <FormLabel>Palette name</FormLabel>
              <Input value={name} onChange={(event) => setName(event.target.value)} />
            </FormControl>
            <FormControl>
              <FormLabel>Colors</FormLabel>
              <Textarea
                placeholder='["#001219","#005f73","#0a9396","#94d2bd","#e9d8a6","#ee9b00"]'
                value={rawColors}
                onChange={(event) => setRawColors(event.target.value)}
                rows={5}
                fontFamily="mono"
              />
            </FormControl>
            <Button onClick={save} isLoading={saving} alignSelf="flex-start">
              Save palette
            </Button>
          </VStack>
        </Box>

        <Box>
          <Text fontWeight="semibold" mb={3}>
            Built-in themes
          </Text>
          <VStack align="stretch" spacing={3}>
            {BUILTIN_THEMES.map((theme) => (
              <HStack key={theme.id} bg={bgColor} borderWidth="1px" borderColor={borderColor} borderRadius="lg" p={3}>
                <Box w="24px" h="24px" borderRadius="full" bg={theme.color} borderWidth="1px" />
                <Text flex="1" fontWeight="semibold">
                  {theme.name}
                </Text>
                <Text fontSize="sm" color="gray.500">
                  Built in
                </Text>
              </HStack>
            ))}
          </VStack>
        </Box>

        <Box>
          <Text fontWeight="semibold" mb={3}>
            Saved palettes
          </Text>
          {loading ? (
            <Spinner />
          ) : savedPalettes.length === 0 ? (
            <Text color="gray.500">No extra palettes yet. Paste an array above to add one.</Text>
          ) : (
            <VStack align="stretch" spacing={3}>
              {savedPalettes.map((palette) => (
                <Box key={palette.id} bg={bgColor} borderWidth="1px" borderColor={borderColor} borderRadius="lg" p={3}>
                  {palette.colors.length === 1 ? (
                    <HStack>
                      <Box w="24px" h="24px" borderRadius="full" bg={palette.colors[0]} borderWidth="1px" />
                      <Text flex="1" fontWeight="semibold">
                        {palette.name}
                      </Text>
                      <IconButton aria-label="Delete palette" icon={<CloseIcon />} size="sm" variant="ghost" flexShrink={0} onClick={() => remove(palette.id)} />
                    </HStack>
                  ) : (
                    <>
                      <HStack mb={3}>
                        <Text flex="1" fontWeight="semibold">
                          {palette.name}
                        </Text>
                        <IconButton aria-label="Delete palette" icon={<CloseIcon />} size="sm" variant="ghost" flexShrink={0} onClick={() => remove(palette.id)} />
                      </HStack>
                      <VStack align="stretch" spacing={2}>
                        {palette.colors.map((color, index) => (
                          <HStack key={`${palette.id}-${index}`}>
                            <Box w="24px" h="24px" borderRadius="full" bg={color} borderWidth="1px" />
                            <Text>
                              {palette.name} {index + 1}
                            </Text>
                          </HStack>
                        ))}
                      </VStack>
                    </>
                  )}
                </Box>
              ))}
            </VStack>
          )}
        </Box>
      </VStack>
    </PageShell>
  );
};

export default ColorPaletteSettings;
